import React, { useContext, useState, useEffect } from 'react';
import { Modal, Spin, Input, List, Avatar, Button, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import CustomerProductContext from '../../ContextFiles/CustomerProductContext';
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';

const CUSTOMER_USERNAME = localStorage.getItem('username');

const ShopifyUploadModal = ({ visible, onCancel, productId }) => {
    const { productAssetData, product_id } = useContext(CustomerProductContext);
    
    const [shopifyUploading, setShopifyUploading] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState('');
    const [autoMatchedProduct, setAutoMatchedProduct] = useState(null);
    const [manualSearchTerm, setManualSearchTerm] = useState('');
    const [manualSearchLoading, setManualSearchLoading] = useState(false);
    const [manualResults, setManualResults] = useState([]);
    const [selectedShopifyProduct, setSelectedShopifyProduct] = useState(null);
    const [productIdentifiers, setProductIdentifiers] = useState({ sku: null, upc: null });
    const [hasSearched, setHasSearched] = useState(false);

    const targetProductId = productId || product_id;

    const handleShopifyUpload = async () => {
        const target = selectedShopifyProduct || autoMatchedProduct;
        if (!target || !target.id) {
            alert('Please select a Shopify product to attach the 3D model.');
            return;
        }
        setShopifyUploading(true);
        try {
            const payload = {
                model_url: `https://all3d.s3.us-west-2.amazonaws.com/product_assets/glb/${targetProductId}.glb`,
                product_id: targetProductId,
                username: CUSTOMER_USERNAME,
                shopify_product_id: target.id,
                handle: target.handle,
            };
            const response = await axios.post(ENVIRONMENT.SHOPIFY_UPLOAD_MODEL, payload);
            message.success('3D model uploaded to Shopify successfully!');
            closeModal();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error uploading model to Shopify.';
            alert(errorMessage);
        } finally {
            setShopifyUploading(false);
        }
    };

    const closeModal = () => {
        onCancel();
        setShopifyUploading(false);
        setLookupLoading(false);
        setLookupError('');
        setAutoMatchedProduct(null);
        setManualSearchTerm('');
        setManualResults([]);
        setManualSearchLoading(false);
        setSelectedShopifyProduct(null);
        setHasSearched(false);
    };

    const ensureProductIdentifiersThenLookup = async (useProductId = null) => {
        const targetProductId = useProductId || product_id;
        if (!targetProductId) return;
        try {
            const hasValidSku = productIdentifiers.sku && productIdentifiers.sku.trim();
            const hasValidUpc = productIdentifiers.upc && productIdentifiers.upc.trim();
            if (hasValidSku || hasValidUpc) {
                await autoLookupShopifyProduct();
                return;
            }
            let sku = (productAssetData?.brand_id && productAssetData.brand_id.trim()) ? productAssetData.brand_id : null;
            let upc = (productAssetData?.gtin && productAssetData.gtin.trim()) ? productAssetData.gtin : null;

            if (!sku && !upc) {
                try {
                    const response = await axios.post(ENVIRONMENT.FETCH_PRODUCT, {
                        product_id: targetProductId,
                        username: CUSTOMER_USERNAME
                    });
                    const data = response.data;
                    const payload = data?.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
                    const fetchedSku = (payload?.brand_id && String(payload.brand_id).trim()) ? String(payload.brand_id).trim() : null;
                    const fetchedUpc = (payload?.gtin && String(payload.gtin).trim()) ? String(payload.gtin).trim() : null;
                    if (fetchedSku) sku = fetchedSku;
                    if (fetchedUpc) upc = fetchedUpc;
                } catch (e) {
                    // fall through
                }
            }
            setProductIdentifiers({ sku, upc });
        } catch (e) {
            // fall through
        } finally {
            await autoLookupShopifyProduct();
        }
    };

    const autoLookupShopifyProduct = async () => {
        try {
            setLookupLoading(true);
            setLookupError('');
            setAutoMatchedProduct(null);
            setSelectedShopifyProduct(null);
            
            const rawSku = productIdentifiers.sku ?? productAssetData?.brand_id;
            const rawUpc = productIdentifiers.upc ?? productAssetData?.gtin;
            const sku = (rawSku && String(rawSku).trim()) ? String(rawSku).trim() : null;
            const upc = (rawUpc && String(rawUpc).trim()) ? String(rawUpc).trim() : null;

            if (!sku && !upc) {
                setLookupLoading(false);
                setLookupError('No SKU/UPC found on this item. Please search by title.');
                return;
            }

            try {
                const response = await axios.post(ENVIRONMENT.SHOPIFY_FIND_PRODUCT, {
                    username: CUSTOMER_USERNAME,
                    sku: sku || undefined,
                    upc: upc || undefined,
                });
                const data = response.data;
                const rawData = data?.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
                const match = Array.isArray(rawData?.products) ? rawData.products[0] : (rawData?.product || (rawData?.id ? rawData : null));
                
                if (match && match.id) {
                    setAutoMatchedProduct(match);
                    try {
                        const directSku = (match?.sku && String(match.sku).trim()) ? String(match.sku).trim() : null;
                        const directUpc = ((match?.upc && String(match.upc).trim()) || (match?.barcode && String(match.barcode).trim()))
                            ? String(match.upc || match.barcode).trim() : null;
                        const firstVariant = match?.variants?.edges?.[0]?.node || match?.variants?.[0] || null;
                        const variantSku = (firstVariant?.sku && String(firstVariant.sku).trim()) ? String(firstVariant.sku).trim() : null;
                        const variantUpc = (firstVariant?.barcode && String(firstVariant.barcode).trim()) ? String(firstVariant.barcode).trim() : null;
                        setProductIdentifiers({
                            sku: directSku || variantSku || null,
                            upc: directUpc || variantUpc || null,
                        });
                    } catch (e) {
                        // ignore parsing issues
                    }
                    setLookupError('');
                } else {
                    setLookupError('No matching product found by SKU/UPC. Please search by title.');
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Lookup failed. Please search by title.';
                setLookupError(errorMessage);
            }
        } catch (e) {
            setLookupError('Lookup failed. Please search by title.');
        } finally {
            setLookupLoading(false);
        }
    };

    const handleManualSearch = async () => {
        if (!manualSearchTerm || manualSearchTerm.length < 2) {
            message.warning('Please enter at least 2 characters to search.');
            return;
        }
        setHasSearched(true);
        try {
            setManualSearchLoading(true);
            setManualResults([]);
            const response = await axios.post(ENVIRONMENT.SHOPIFY_SEARCH_PRODUCTS, {
                username: CUSTOMER_USERNAME,
                query: manualSearchTerm,
            });
            const data = response.data;
            const products = data?.body?.products || data?.products || [];
            setManualResults(products);
        } catch (e) {
            setManualResults([]);
        } finally {
            setManualSearchLoading(false);
        }
    };

    useEffect(() => {
        if (!visible || !targetProductId) return;

        const checkAndLookup = () => {
            const isAuthorized = localStorage.getItem('shopify_authorized') === 'true';
            if (isAuthorized && targetProductId) {
                ensureProductIdentifiersThenLookup(targetProductId).catch(() => { });
                return true;
            }
            return false;
        };

        if (checkAndLookup()) return;

        let retryCount = 0;
        const maxRetries = 10;
        const retryInterval = setInterval(() => {
            retryCount++;
            if (checkAndLookup() || retryCount >= maxRetries) {
                clearInterval(retryInterval);
            }
        }, 500);

        return () => clearInterval(retryInterval);
    }, [visible, targetProductId, productAssetData?.brand_id, productAssetData?.gtin]);
    const getModalTitle = () => {
        if (autoMatchedProduct || selectedShopifyProduct) {
            return 'Shopify Product Details';
        }
        if (lookupError && !lookupLoading) {
            return 'Product not found - Search by title';
        }
        return 'Upload/Replace 3D Model on Shopify';
    };

    return (
        <Modal
            title={getModalTitle()}
            visible={visible}
            onCancel={closeModal}
            footer={null}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {lookupLoading && (
                    <div className="manrope f-14" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Spin size="small" />
                        Looking up product by SKU/UPC...
                    </div>
                )}
                {!lookupLoading && (autoMatchedProduct || selectedShopifyProduct) && (() => {
                    const matched = selectedShopifyProduct || autoMatchedProduct;
                    const shopifySku = matched?.sku ?? matched?.variants?.edges?.[0]?.node?.sku ?? matched?.variants?.[0]?.sku ?? null;
                    const shopifyUpc = matched?.upc ?? matched?.barcode ?? matched?.variants?.edges?.[0]?.node?.barcode ?? matched?.variants?.[0]?.barcode ?? null;
                    const derivedSku = shopifySku ?? productIdentifiers.sku ?? productAssetData?.brand_id ?? null;
                    const derivedUpc = shopifyUpc ?? productIdentifiers.upc ?? productAssetData?.gtin ?? null;
                    return (
                        <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 8,
                            padding: 20,
                            backgroundColor: '#fafafa',
                            marginBottom: 8
                        }}>
                            <div className="manrope f-14" style={{ display: 'grid', rowGap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>Title:</span>
                                    <span style={{ color: '#141414' }}>{matched.title || '—'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>Handle:</span>
                                    <span style={{ color: '#141414' }}>{matched.handle || '—'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>SKU:</span>
                                    <span style={{ color: '#141414' }}>{derivedSku || '—'}</span>
                                </div>
                                {derivedUpc && (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span className="w-600" style={{ minWidth: 80, color: '#595959' }}>UPC:</span>
                                        <span style={{ color: '#141414' }}>{derivedUpc}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}
                {!lookupLoading && !autoMatchedProduct && !selectedShopifyProduct && (
                    <div>
                        <label className="manrope f-14 black-55" style={{ marginBottom: 4 }}>Search by Title</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Input
                                value={manualSearchTerm}
                                onChange={(e) => {
                                    setManualSearchTerm(e.target.value);
                                    setHasSearched(false);
                                    if (!e.target.value) {
                                        setManualResults([]);
                                        setSelectedShopifyProduct(null);
                                    }
                                }}
                                onPressEnter={handleManualSearch}
                                placeholder="Enter product title to search"
                                disabled={shopifyUploading || manualSearchLoading}
                                style={{ flex: 1 }}
                            />
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleManualSearch}
                                disabled={shopifyUploading || manualSearchLoading || !manualSearchTerm || manualSearchTerm.length < 2}
                                loading={manualSearchLoading}
                            >
                                Search
                            </Button>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            {manualSearchLoading ? (
                                <div className="manrope f-13"><Spin size="small" /> Searching...</div>
                            ) : (
                                manualResults && manualResults.length > 0 ? (
                                    <List
                                        size="small"
                                        bordered
                                        dataSource={manualResults}
                                        renderItem={item => (
                                            <List.Item
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setSelectedShopifyProduct(item)}
                                            >
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar shape="square" src={item.image || item.featured_image || undefined}>
                                                            {item.title?.[0] || 'P'}
                                                        </Avatar>
                                                    }
                                                    title={<span className="manrope f-13">{item.title || item.handle}</span>}
                                                    description={<span className="manrope f-12 grey-8c">ID: {item.id}{item.handle ? ` • Handle: ${item.handle}` : ''}</span>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                ) : hasSearched && !manualSearchLoading ? (
                                    <div className="manrope f-13" style={{ color: '#ff4d4f' }}>Match not found.</div>
                                ) : null
                            )}
                        </div>
                    </div>
                )}
                {(autoMatchedProduct || selectedShopifyProduct) && (
                    <Button
                        type="primary"
                        style={{ marginTop: 8, width: "100%" }}
                        onClick={handleShopifyUpload}
                        disabled={shopifyUploading}
                    >
                        {shopifyUploading ? <Spin size="small" /> : 'Upload/Replace 3D Model'}
                    </Button>
                )}
            </div>
        </Modal>
    );
};

export default ShopifyUploadModal;

